import styled from 'styled-components';
import { Table as AntdTable } from 'antd';
import { hexToRGB } from '@app/utils/utils';

/**
 * Shared look for every data table in the admin panel: dense rows, small
 * tracked-down type, hairline separators, no vertical grid.
 *
 * Colours are derived from the active theme rather than hard-coded, so the
 * same rules hold in light and dark mode -- `text.main` is #ffffff in dark and
 * #404040 in light, so alpha-blending it gives a separator that reads correctly
 * either way.
 *
 * Selectors repeat the component class (`&&`) because AntD's own table rules
 * are specific enough (0,4,0) that a single class silently loses to them.
 */
export const Table = styled(AntdTable)`
  ${({ theme }) => {
    const ink = theme.colors.text.main;
    const hairline = hexToRGB(ink, 0.07);
    const headRule = hexToRGB(ink, 0.1);
    const hover = hexToRGB(ink, 0.035);
    const muted = theme.colors.text.light;

    return `
    && .ant-table {
      background: transparent;
      font-size: 14px;
    }

    /* Column headings sit at body size like everything else; they are set
       apart by weight and a muted colour rather than by being smaller. */
    && .ant-table-thead > tr > th {
      background: transparent;
      border-bottom: 1px solid ${headRule};
      padding: 10px 12px;
      font-size: 14px;
      font-weight: 600;
      color: ${muted};
      white-space: nowrap;
    }

    /* AntD draws a divider between headers; it adds noise at this density. */
    && .ant-table-thead > tr > th::before {
      display: none !important;
    }

    && .ant-table-thead > tr > th .anticon {
      color: ${muted};
    }

    && .ant-table-tbody > tr > td {
      padding: 10px 12px;
      border-bottom: 1px solid ${hairline};
      font-size: 14px;
      color: ${theme.colors.text.main};
      vertical-align: middle;
    }

    && .ant-table-tbody > tr:last-child > td {
      border-bottom: none;
    }

    && .ant-table-tbody > tr.ant-table-row:hover > td {
      background: ${hover};
    }

    && .ant-table-placeholder:hover > td {
      background: transparent;
    }

    /* Row actions stay quiet until the row is under the cursor. */
    && .ant-table-tbody > tr > td .row-action {
      opacity: 0.75;
      transition: opacity 0.15s ease;
    }
    && .ant-table-tbody > tr:hover > td .row-action {
      opacity: 1;
    }

    && .ant-table-column-sort {
      background-color: transparent;
    }

    && .ant-pagination {
      margin: 14px 0;
    }

    && .ant-pagination-item,
    && .ant-pagination-prev,
    && .ant-pagination-next,
    && .ant-pagination-jump-prev,
    && .ant-pagination-jump-next {
      min-width: 28px;
      height: 28px;
      line-height: 26px;
      font-size: 14px;
      border-radius: 6px;
      font-variant-numeric: tabular-nums;
    }

    && .ant-pagination-prev .ant-pagination-item-link,
    && .ant-pagination-next .ant-pagination-item-link {
      border-radius: 6px;
    }

    /* Retained from the previous styling. */
    && .ant-table-row-expand-icon {
      min-height: 1.25rem;
      min-width: 1.25rem;
      border-radius: 0.1875rem;
      margin-top: 0;
    }

    && .ant-checkbox-inner {
      border-radius: 0.1875rem;
      height: 1.25rem;
      width: 1.25rem;
      border: 1px solid ${theme.colors.main.primary};
    }

    && .editable-row .ant-form-item-explain {
      position: absolute;
      top: 100%;
      font-size: 0.75rem;
    }
  `;
  }}
`;
